/* assets/faq-page.js */

(() => {
  "use strict";

  const SELECTORS = {
    root: "[data-faq-page]",
    item: "[data-faq-item]",
    list: "[data-faq-list]",
    categoryList: "[data-faq-category-list]",
    mobileCategoryList: "[data-faq-mobile-category-list]",
    categoryButton: "[data-faq-category-button]",
    mobileCategoryButton: "[data-faq-mobile-category-button]",
    searchInput: "[data-faq-search]",
    searchClear: "[data-faq-search-clear]",
    searchForm: "[data-faq-search-form]",
    searchBox: "[data-faq-search-box]",
    suggestions: "[data-faq-suggestions]",
    results: "[data-faq-results]",
    empty: "[data-faq-empty]",
    pagination: "[data-faq-pagination]",
    trigger: "[data-faq-trigger]",
    answer: "[data-faq-answer]",
    mobileFilter: "[data-faq-mobile-filter]",
    mobileToggle: "[data-faq-mobile-filter-toggle]",
    mobileApply: "[data-faq-mobile-filter-apply]",
    mobileCancel: "[data-faq-mobile-filter-cancel]"
  };

  const MOBILE_MAX = 749;
  const TABLET_MAX = 1140;
  const ALL_KEY = "all";
  const FAQ_ANIMATION_DURATION = 280;

  const normalize = (value) => String(value || "").trim();
  const key = (value) => normalize(value).toLowerCase();

  const escapeHtml = (value) =>
    String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  const isMobile = () => window.matchMedia(`(max-width: ${MOBILE_MAX}px)`).matches;
  const isTablet = () => window.matchMedia(`(min-width: ${MOBILE_MAX + 1}px) and (max-width: ${TABLET_MAX}px)`).matches;

  const arrowLeft = () => `
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
      <path d="M4.16406 10H15.8307" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M4.16406 10L7.4974 13.3333" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M4.16406 10.0001L7.4974 6.66675" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`;

  const arrowRight = () => `
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
      <path d="M4.16406 10H15.8307" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M12.5 13.3333L15.8333 10" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M12.5 6.66675L15.8333 10.0001" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`;

  document.querySelectorAll(SELECTORS.root).forEach(initFaqPage);

  function initFaqPage(root) {
    const items = Array.from(root.querySelectorAll(SELECTORS.item));
    const list = root.querySelector(SELECTORS.list);

    if (!list || !items.length) return;

    const state = {
      category: ALL_KEY,
      pendingCategory: ALL_KEY,
      query: "",
      page: 1,
      breakpoint: getBreakpoint()
    };

    const categories = getCategories(root, items);

    renderCategoryLists(root, categories, state);
    bindEvents(root, state, items, categories);
    updateSearchClearButtons(root);
    render(root, state, items, categories);
  }

  function bindEvents(root, state, items, categories) {
    root.addEventListener("click", (event) => {
      const trigger = event.target.closest(SELECTORS.trigger);
      const categoryButton = event.target.closest(SELECTORS.categoryButton);
      const mobileCategoryButton = event.target.closest(SELECTORS.mobileCategoryButton);
      const paginationButton = event.target.closest("[data-faq-page-number]");
      const prevButton = event.target.closest("[data-faq-page-prev]");
      const nextButton = event.target.closest("[data-faq-page-next]");
      const mobileToggle = event.target.closest(SELECTORS.mobileToggle);
      const mobileApply = event.target.closest(SELECTORS.mobileApply);
      const mobileCancel = event.target.closest(SELECTORS.mobileCancel);
      const suggestion = event.target.closest("[data-faq-suggestion]");
      const searchClear = event.target.closest(SELECTORS.searchClear);

      if (searchClear) {
        event.preventDefault();
        state.query = "";
        state.page = 1;
        syncSearchInputs(root, "");
        hideSuggestions(root);
        updateSearchClearButtons(root);
        render(root, state, items, categories);

        const box = searchClear.closest(SELECTORS.searchBox);
        const input = box?.querySelector(SELECTORS.searchInput);
        input?.focus({ preventScroll: true });
        return;
      }

      if (trigger) {
        event.preventDefault();
        const item = trigger.closest(SELECTORS.item);
        const group = trigger.closest("[data-faq-group]") || root.querySelector(SELECTORS.list);
        if (item && group) toggleFaqItem(group, item);
        return;
      }

      if (categoryButton) {
        event.preventDefault();
        state.category = categoryButton.dataset.faqCategoryButton || ALL_KEY;
        state.pendingCategory = state.category;
        state.page = 1;
        renderCategoryLists(root, categories, state);
        render(root, state, items, categories);
        return;
      }

      if (mobileCategoryButton) {
        event.preventDefault();
        state.pendingCategory = mobileCategoryButton.dataset.faqMobileCategoryButton || ALL_KEY;
        updateMobileCategoryState(root, state);
        return;
      }

      if (paginationButton || prevButton || nextButton) {
        event.preventDefault();
        const totalPages = getTotalPages(root, state, items);

        if (totalPages <= 1) return;

        if (paginationButton) {
          const page = Number(paginationButton.dataset.faqPageNumber);
          if (!Number.isNaN(page)) state.page = page;
        } else if (prevButton && state.page > 1) {
          state.page -= 1;
        } else if (nextButton && state.page < totalPages) {
          state.page += 1;
        }

        render(root, state, items, categories);
        scrollToResults(root);
        return;
      }

      if (mobileToggle) {
        event.preventDefault();
        state.pendingCategory = state.category;
        openMobileFilter(root, state);
        return;
      }

      if (mobileApply) {
        event.preventDefault();
        state.category = state.pendingCategory;
        state.page = 1;
        closeMobileFilter(root);
        renderCategoryLists(root, categories, state);
        render(root, state, items, categories);
        return;
      }

      if (mobileCancel) {
        event.preventDefault();
        state.pendingCategory = state.category;
        closeMobileFilter(root);
        updateMobileCategoryState(root, state);
        return;
      }

      if (suggestion) {
        event.preventDefault();
        state.query = suggestion.dataset.faqSuggestion || "";
        state.page = 1;
        syncSearchInputs(root, state.query);
        hideSuggestions(root);
        updateSearchClearButtons(root);
        render(root, state, items, categories);
      }
    });

    root.querySelectorAll(SELECTORS.searchForm).forEach((form) => {
      form.addEventListener("submit", (event) => {
        event.preventDefault();
        const input = form.querySelector(SELECTORS.searchInput);
        state.query = normalize(input?.value);
        state.page = 1;
        syncSearchInputs(root, state.query);
        hideSuggestions(root);
        updateSearchClearButtons(root);
        render(root, state, items, categories);
      });
    });

    root.querySelectorAll(SELECTORS.searchInput).forEach((input) => {
      input.addEventListener("input", () => {
        state.query = normalize(input.value);
        state.page = 1;
        syncSearchInputs(root, state.query, input);
        updateSearchClearButtons(root);
        render(root, state, items, categories);
        renderSuggestions(root, state, items, input);
      });

      input.addEventListener("focus", () => {
        renderSuggestions(root, state, items, input);
      });
    });

    document.addEventListener("click", (event) => {
      if (!root.contains(event.target)) {
        hideSuggestions(root);
        return;
      }

      if (!event.target.closest(SELECTORS.searchBox)) {
        hideSuggestions(root);
      }
    });

    document.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") return;
      hideSuggestions(root);
      closeMobileFilter(root);
    });

    let resizeTimer = null;

    window.addEventListener("resize", () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const next = getBreakpoint();
        if (next === state.breakpoint) return;
        state.breakpoint = next;
        state.page = 1;
        render(root, state, items, categories);
      }, 100);
    });
  }

  function getCategories(root, items) {
    const script = root.querySelector("[data-faq-categories-json]");
    let raw = "";

    if (script) {
      try {
        raw = JSON.parse(script.textContent || '""');
      } catch (error) {
        raw = "";
      }
    }

    const configured = String(raw || "")
      .split(/\r?\n|,/)
      .map(normalize)
      .filter(Boolean);

    const seen = new Set();
    const categories = [];

    const pushCategory = (label) => {
      const labelText = normalize(label);
      const itemKey = key(labelText);
      if (!itemKey || seen.has(itemKey)) return;
      seen.add(itemKey);
      categories.push({ key: itemKey === ALL_KEY ? ALL_KEY : itemKey, label: labelText });
    };

    pushCategory("All");
    configured.forEach(pushCategory);

    items.forEach((item) => {
      pushCategory(item.dataset.category || "General");
    });

    return categories;
  }

  function renderCategoryLists(root, categories, state) {
    const desktop = root.querySelector(SELECTORS.categoryList);
    const mobile = root.querySelector(SELECTORS.mobileCategoryList);

    const desktopHtml = categories
      .map((category) => `
        <button
          class="faq-page__category-button${state.category === category.key ? " is-active" : ""}"
          type="button"
          data-faq-category-button="${escapeHtml(category.key)}"
          aria-current="${state.category === category.key ? "true" : "false"}"
        >${escapeHtml(category.label)}</button>`)
      .join("");

    const mobileHtml = categories
      .map((category) => `
        <button
          class="faq-page__mobile-category-button${state.pendingCategory === category.key ? " is-active" : ""}"
          type="button"
          data-faq-mobile-category-button="${escapeHtml(category.key)}"
          aria-current="${state.pendingCategory === category.key ? "true" : "false"}"
        >${escapeHtml(category.label)}</button>`)
      .join("");

    if (desktop) desktop.innerHTML = desktopHtml;
    if (mobile) mobile.innerHTML = mobileHtml;
  }

  function updateMobileCategoryState(root, state) {
    root.querySelectorAll(SELECTORS.mobileCategoryButton).forEach((button) => {
      const active = button.dataset.faqMobileCategoryButton === state.pendingCategory;
      button.classList.toggle("is-active", active);
      button.setAttribute("aria-current", active ? "true" : "false");
    });
  }

  function render(root, state, items, categories) {
    const list = root.querySelector(SELECTORS.list);
    const results = root.querySelector(SELECTORS.results);
    const empty = root.querySelector(SELECTORS.empty);
    const pagination = root.querySelector(SELECTORS.pagination);

    if (!list) return;

    const filtered = getFilteredItems(state, items);
    const hasQuery = key(state.query).length > 0;

    state.page = 1;

    if (results) {
      if (hasQuery && filtered.length > 0) {
        const template = root.dataset.resultsTemplate || "We found ([[count]]) results";
        results.textContent = template.replace("[[count]]", String(filtered.length));
        results.hidden = false;
      } else {
        results.textContent = "";
        results.hidden = true;
      }
    }

    if (pagination) {
      pagination.hidden = true;
      pagination.innerHTML = "";
    }

    if (empty) empty.hidden = filtered.length !== 0;

    list.hidden = filtered.length === 0;
    list.classList.toggle("is-search-mode", hasQuery);
    list.replaceChildren();

    if (filtered.length) {
      renderGroups(list, filtered, categories, hasQuery);
    }
  }

  function renderGroups(list, visibleItems, categories, isSearchMode = false) {
    if (isSearchMode) {
      const groupNode = document.createElement("div");
      groupNode.className = "faq-page__group faq-page__group--search";
      groupNode.dataset.faqGroup = "search-results";

      visibleItems.forEach((item, index) => {
        syncItemState(item, index === 0);
        groupNode.appendChild(item);
      });

      list.appendChild(groupNode);
      return;
    }

    const categoryOrder = categories.filter((category) => category.key !== ALL_KEY);
    const itemGroups = new Map();

    visibleItems.forEach((item) => {
      const categoryKey = key(item.dataset.category || "General");
      if (!itemGroups.has(categoryKey)) itemGroups.set(categoryKey, []);
      itemGroups.get(categoryKey).push(item);
    });

    const orderedGroups = [];

    categoryOrder.forEach((category) => {
      if (itemGroups.has(category.key)) {
        orderedGroups.push({ key: category.key, label: category.label, items: itemGroups.get(category.key) });
        itemGroups.delete(category.key);
      }
    });

    itemGroups.forEach((groupItems, groupKey) => {
      orderedGroups.push({ key: groupKey, label: groupItems[0]?.dataset.category || "General", items: groupItems });
    });

    orderedGroups.forEach((group) => {
      const groupNode = document.createElement("div");
      groupNode.className = "faq-page__group";
      groupNode.dataset.faqGroup = group.key;

      if (!isSearchMode) {
        const heading = document.createElement("h2");
        heading.className = "faq-page__group-title";
        heading.textContent = group.label;
        groupNode.appendChild(heading);
      }

      const preferredOpen = group.items.find((item) => item.hasAttribute("open"));
      const openItem = preferredOpen || group.items[0];

      group.items.forEach((item) => {
        syncItemState(item, item === openItem);
        groupNode.appendChild(item);
      });

      list.appendChild(groupNode);
    });
  }

  function getFilteredItems(state, items) {
    const query = key(state.query);

    return items.filter((item) => {
      const itemCategoryKey = key(item.dataset.category || "General");
      const categoryPass = state.category === ALL_KEY || itemCategoryKey === state.category;

      if (!categoryPass) return false;
      if (!query) return true;

      const haystack = [item.dataset.question, item.dataset.answer, item.dataset.category]
        .map(key)
        .join(" ");

      return haystack.includes(query);
    });
  }

  function getPerPage(root) {
    const desktop = Number(root.dataset.perPageDesktop) || 12;
    const tablet = Number(root.dataset.perPageTablet) || 6;
    const mobile = Number(root.dataset.perPageMobile) || 4;

    if (isMobile()) return mobile;
    if (isTablet()) return tablet;
    return desktop;
  }

  function getTotalPages(root, state, items) {
    const total = getFilteredItems(state, items).length;
    return Math.max(1, Math.ceil(total / getPerPage(root)));
  }

  function renderPagination(root, state, totalItems, perPage) {
    const pagination = root.querySelector(SELECTORS.pagination);
    if (!pagination) return;

    const totalPages = Math.ceil(totalItems / perPage);

    if (totalPages <= 1) {
      pagination.hidden = true;
      pagination.innerHTML = "";
      return;
    }

    const pages = getVisiblePages(state.page, totalPages);
    const prevDisabled = state.page === 1;
    const nextDisabled = state.page === totalPages;

    pagination.hidden = false;
    pagination.innerHTML = `
      <button
        type="button"
        class="faq-page__pagination-arrow"
        data-faq-page-prev
        ${prevDisabled ? "disabled" : ""}
        aria-label="Previous FAQ page"
      >${arrowLeft()}</button>

      ${pages
        .map((page) => {
          if (page === "...") {
            return '<span class="faq-page__pagination-ellipsis" aria-hidden="true">...</span>';
          }

          const active = page === state.page;

          return `
            <button
              type="button"
              class="faq-page__pagination-button${active ? " is-active" : ""}"
              data-faq-page-number="${page}"
              aria-current="${active ? "page" : "false"}"
            >${page}</button>`;
        })
        .join("")}

      <button
        type="button"
        class="faq-page__pagination-arrow"
        data-faq-page-next
        ${nextDisabled ? "disabled" : ""}
        aria-label="Next FAQ page"
      >${arrowRight()}</button>`;
  }

  function getVisiblePages(current, total) {
    if (total <= 5) return Array.from({ length: total }, (_, index) => index + 1);
    if (current <= 3) return [1, 2, 3, 4, "...", total];
    if (current >= total - 2) return [1, "...", total - 3, total - 2, total - 1, total];
    return [1, "...", current - 1, current, current + 1, "...", total];
  }

  function getItemParts(item) {
    return {
      trigger: item.querySelector(SELECTORS.trigger),
      answer: item.querySelector(SELECTORS.answer)
    };
  }

  function syncItemState(item, expanded) {
    const parts = getItemParts(item);
    if (!parts.trigger || !parts.answer) return;

    item.open = expanded;
    item.classList.remove("is-opening", "is-closing");
    item.dataset.faqAnimating = "false";
    parts.trigger.setAttribute("aria-expanded", expanded ? "true" : "false");
    parts.answer.setAttribute("aria-hidden", expanded ? "false" : "true");
    parts.answer.style.height = expanded ? "auto" : "0px";
  }

  function animateItem(item, expanded, onComplete) {
    const parts = getItemParts(item);
    const trigger = parts.trigger;
    const answer = parts.answer;
    let isFinished = false;
    let fallbackTimer = null;

    if (!trigger || !answer) {
      if (typeof onComplete === "function") onComplete();
      return;
    }

    function finish() {
      if (isFinished) return;
      isFinished = true;

      window.clearTimeout(fallbackTimer);
      answer.removeEventListener("transitionend", onTransitionEnd);

      item.open = expanded;
      item.classList.remove("is-opening", "is-closing");
      item.dataset.faqAnimating = "false";
      trigger.setAttribute("aria-expanded", expanded ? "true" : "false");
      answer.setAttribute("aria-hidden", expanded ? "false" : "true");
      answer.style.height = expanded ? "auto" : "0px";

      if (typeof onComplete === "function") onComplete();
    }

    function onTransitionEnd(event) {
      if (event.target !== answer || event.propertyName !== "height") return;
      finish();
    }

    item.dataset.faqAnimating = "true";
    item.classList.toggle("is-opening", expanded);
    item.classList.toggle("is-closing", !expanded);

    if (expanded) {
      item.open = true;
      trigger.setAttribute("aria-expanded", "true");
      answer.setAttribute("aria-hidden", "false");
      answer.style.height = "0px";
      answer.offsetHeight;

      fallbackTimer = window.setTimeout(finish, FAQ_ANIMATION_DURATION + 80);
      answer.addEventListener("transitionend", onTransitionEnd);

      window.requestAnimationFrame(() => {
        answer.style.height = `${answer.scrollHeight}px`;
      });

      return;
    }

    answer.style.height = `${answer.scrollHeight}px`;
    answer.offsetHeight;

    fallbackTimer = window.setTimeout(finish, FAQ_ANIMATION_DURATION + 80);
    answer.addEventListener("transitionend", onTransitionEnd);

    window.requestAnimationFrame(() => {
      answer.style.height = "0px";
    });
  }

  function toggleFaqItem(group, item) {
    if (group.dataset.faqAnimating === "true") return;

    const shouldOpen = !item.open;
    let pendingAnimations = 0;

    function startAnimation() {
      pendingAnimations += 1;
      group.dataset.faqAnimating = "true";
    }

    function finishAnimation() {
      pendingAnimations -= 1;

      if (pendingAnimations <= 0) {
        group.dataset.faqAnimating = "false";
      }
    }

    if (shouldOpen) {
      group.querySelectorAll(SELECTORS.item).forEach((otherItem) => {
        if (otherItem !== item && otherItem.open) {
          startAnimation();
          animateItem(otherItem, false, finishAnimation);
        }
      });
    }

    startAnimation();
    animateItem(item, shouldOpen, finishAnimation);
  }

  function syncSearchInputs(root, value, exceptInput = null) {
    root.querySelectorAll(SELECTORS.searchInput).forEach((input) => {
      if (input !== exceptInput) input.value = value;
    });
  }

  function updateSearchClearButtons(root) {
    root.querySelectorAll(SELECTORS.searchBox).forEach((box) => {
      const input = box.querySelector(SELECTORS.searchInput);
      const clear = box.querySelector(SELECTORS.searchClear);

      if (!input || !clear) return;

      clear.hidden = normalize(input.value).length === 0;
    });
  }

  function renderSuggestions(root, state, items, input) {
    hideSuggestions(root, input);

    const query = key(state.query);
    const box = input.closest(SELECTORS.searchBox);
    const suggestions = box?.querySelector(SELECTORS.suggestions);

    if (!suggestions || query.length < 2) return;

    const matches = items
      .filter((item) => key(item.dataset.question).includes(query))
      .slice(0, 8);

    if (!matches.length) return;

    suggestions.innerHTML = matches
      .map((item) => `
        <button
          type="button"
          class="faq-page__suggestion"
          data-faq-suggestion="${escapeHtml(item.dataset.question || "")}"
        >${escapeHtml(item.dataset.question || "")}</button>`)
      .join("");

    suggestions.hidden = false;
  }

  function hideSuggestions(root, exceptInput = null) {
    root.querySelectorAll(SELECTORS.suggestions).forEach((suggestions) => {
      if (exceptInput && suggestions.closest(SELECTORS.searchBox)?.contains(exceptInput)) return;
      suggestions.hidden = true;
      suggestions.innerHTML = "";
    });
  }

  function openMobileFilter(root, state) {
    const panel = root.querySelector(SELECTORS.mobileFilter);
    const toggle = root.querySelector(SELECTORS.mobileToggle);

    if (!panel) return;

    const header = document.querySelector(".site-header");
    const headerHeight = header ? Math.ceil(header.getBoundingClientRect().height) : 68;

    root.style.setProperty("--faq-mobile-header-height", `${headerHeight}px`);

    panel.hidden = false;
    document.body.classList.add("faq-mobile-filter-open");
    toggle?.setAttribute("aria-expanded", "true");
    updateMobileCategoryState(root, state);
  }

  function closeMobileFilter(root) {
    const panel = root.querySelector(SELECTORS.mobileFilter);
    const toggle = root.querySelector(SELECTORS.mobileToggle);

    if (!panel) return;

    panel.hidden = true;
    document.body.classList.remove("faq-mobile-filter-open");
    toggle?.setAttribute("aria-expanded", "false");
  }

  function scrollToResults(root) {
    const top = root.querySelector(SELECTORS.results) || root;
    const rect = top.getBoundingClientRect();

    if (rect.top >= 0 && rect.top < window.innerHeight * 0.35) return;

    window.scrollTo({
      top: window.scrollY + rect.top - 90,
      behavior: "smooth"
    });
  }

  function getBreakpoint() {
    if (isMobile()) return "mobile";
    if (isTablet()) return "tablet";
    return "desktop";
  }
})();
